import { Col, Row } from "react-bootstrap";
import Card from "react-bootstrap/Card";
import Pagination from "react-bootstrap/Pagination";
import "./candidatesBox.css";
import { useLocation } from "react-router-dom";
import { getAsset } from "../../helpers/helper";
import Skeleton from "react-loading-skeleton";
import "react-loading-skeleton/dist/skeleton.css";
import { SuccessNotify, FailedNotify } from "../../constants/tostify";
import { SadIcon } from "../../assets";
import {
  useAcceptConnectionRequestMutation,
  useRejectConnectionRequestMutation,
  useCancelConnectionRequestMutation,
} from "../../features/connection/connectionApiSlice";
import { useState, useEffect, useRef } from "react";
import dummyAvatar from "@/assets/images/dummy/user-avatar-img.jpg";

// Skeleton card that mirrors the real CandidateBox card shape
const CandidateSkeletonCard = () => (
  <Col md={3} lg={3} className="px-md-1 pb-4">
    <Card className="border pt-2 pb-2 h-100">
      <div className="py-1 d-flex flex-column align-items-center justify-content-between h-100">
        <div className="text-center w-100">
          <div className="candidateImg-2 m-auto">
            <Skeleton
              circle
              width="100%"
              height="100%"
              style={{ display: "block" }}
            />
          </div>
          <Skeleton width={100} height={13} className="mt-3" />
        </div>
        <div className="d-flex justify-content-center mt-3 w-100 gap-1">
          <Skeleton width={70} height={32} borderRadius={6} />
          <Skeleton width={70} height={32} borderRadius={6} />
        </div>
      </div>
    </Card>
  </Col>
);

const CandidatesBox = ({
  pendingData,
  loading,
  fetchPendingConnections,
  activeTab = "received",
}) => {
  const location = useLocation();

  const [acceptConnection, { isLoading: isAccepting }] =
    useAcceptConnectionRequestMutation();
  const [rejectConnection, { isLoading: isRejecting }] =
    useRejectConnectionRequestMutation();
  const [cancelConnection, { isLoading: isCanceling }] =
    useCancelConnectionRequestMutation();

  // ── Local copies of the two lists so we can remove items instantly
  const [localPending, setLocalPending] = useState([]);
  const [localRequested, setLocalRequested] = useState([]);
  // Survive stale RTK refetches so accepted/rejected cards stay gone
  const dismissedPendingRef = useRef(new Set());
  const dismissedRequestedRef = useRef(new Set());

  /** Normalize Mongo id fields that may arrive as string | {_id} | ObjectId */
  const toId = (value) => {
    if (value == null) return null;
    if (typeof value === "object") {
      const nested = value._id ?? value.id;
      if (nested == null) return null;
      value = nested;
    }
    const text = String(value).trim();
    if (
      !text ||
      text === "null" ||
      text === "undefined" ||
      text === "[object Object]"
    ) {
      return null;
    }
    return text;
  };

  /** Canonical business type — the server stamps several marketer spellings. */
  const originBT = (bt) => {
    const n = String(bt || "")
      .toLowerCase()
      .replace(/[\s_-]+/g, "");
    if (n === "marketer" || n === "marketershop" || n === "marketerprofile") {
      return "marketerprofile";
    }
    return n;
  };
  const originKey = (row, side) =>
    side === "sender"
      ? [
          toId(row?.senderid) || "",
          toId(row?.sendershopid) || toId(row?.senderShopContextId) || "null",
          originBT(row?.senderBusinessType),
        ].join("|")
      : [
          toId(row?.recieverid) || "",
          toId(row?.recievershopid) ||
            toId(row?.recieverShopContextId) ||
            "null",
          originBT(row?.businessType),
        ].join("|");

  const partyKey = (row) =>
    [originKey(row, "sender"), originKey(row, "reciever")].join("#");

  // Collapse by the other party's origin profile only (same shop → many of our
  // profiles still shows as one card on Received / Sent lists).
  const dedupeByParty = (rows, mode) => {
    if (!Array.isArray(rows)) return [];
    const seen = new Set();
    const unique = [];
    for (const row of rows) {
      const key = originKey(row, mode === "pending" ? "sender" : "reciever");
      if (seen.has(key)) continue;
      seen.add(key);
      unique.push(row);
    }
    return unique;
  };

  const buildConnectionPayload = (row) => {
    const senderid = toId(row?.senderid);
    const recieverid = toId(row?.recieverid);
    const sendershopid = toId(row?.sendershopid);
    const recievershopid = toId(row?.recievershopid);
    const businessType = row?.businessType || undefined;
    const senderBusinessType = row?.senderBusinessType || undefined;
    const senderShopContextId = toId(row?.senderShopContextId);
    const recieverShopContextId = toId(row?.recieverShopContextId);
    return {
      senderid,
      recieverid,
      ...(sendershopid ? { sendershopid } : { sendershopid: null }),
      ...(recievershopid ? { recievershopid } : { recievershopid: null }),
      ...(businessType ? { businessType } : {}),
      ...(senderBusinessType ? { senderBusinessType } : {}),
      // Keeps the accepted row tied to the shop the request came from, which is
      // the only shop link a generalPublic request has.
      ...(senderShopContextId ? { senderShopContextId } : {}),
      ...(recieverShopContextId ? { recieverShopContextId } : {}),
      ...(row?.asMarketerProfile === true || row?.fromMarketerProfile === true
        ? { asMarketerProfile: true }
        : {}),
    };
  };
  const sameSenderShop = (a, b) =>
    originKey(a, "sender") === originKey(b, "sender");

  const sameReceiverShop = (a, b) =>
    originKey(a, "reciever") === originKey(b, "reciever");

  useEffect(() => {
    const incoming = Array.isArray(pendingData?.pending)
      ? pendingData.pending
      : [];
    setLocalPending(
      incoming.filter((row) => !dismissedPendingRef.current.has(partyKey(row))),
    );
  }, [pendingData?.pending]);

  useEffect(() => {
    const incoming = Array.isArray(pendingData?.requested)
      ? pendingData.requested
      : [];
    setLocalRequested(
      incoming.filter(
        (row) => !dismissedRequestedRef.current.has(partyKey(row)),
      ),
    );
  }, [pendingData?.requested]);

  // Human-friendly label for a raw businessType key (e.g. "generalPublic" -> "General Public")
  const formatBusinessTypeTitle = (bt) => {
    if (!bt) return "";
    const n = String(bt)
      .toLowerCase()
      .replace(/[\s_-]+/g, "");
    if (n === "generalpublic") return "General Public";
    if (n === "marketerprofile" || n === "marketer" || n === "marketershop") {
      return "Marketer Profile";
    }
    if (bt === "businessPropertyCompany") return "Business Property";
    // Convert camelCase to Title Case (e.g., homemadeShop -> Homemade Shop)
    const result = bt.replace(/([A-Z])/g, " $1");
    return result.charAt(0).toUpperCase() + result.slice(1);
  };
  const getPlatformLabels = (box) => {
    const isSent = activeTab === "sent";
    if (isSent) {
      const from = formatBusinessTypeTitle(
        box?.sentFrom || box?.senderBusinessType,
      );
      const to = formatBusinessTypeTitle(box?.sentTo || box?.businessType);
      const labels = [];
      if (from) labels.push(`Sent from: ${from}`);
      if (to) labels.push(`Sent to: ${to}`);
      return labels;
    }

    const from = formatBusinessTypeTitle(
      box?.receivedFrom || box?.senderBusinessType,
    );
    const on = formatBusinessTypeTitle(box?.sentTo || box?.businessType);
    const labels = [];
    // if (from) labels.push(`Received from: ${from}`);
    if (on) labels.push(`Received on: ${on}`);
    return labels;
  };

  const resolveImage = (img) => {
    if (!img) return null;
    if (Array.isArray(img)) {
      const first = img.find((v) => v && v !== "N/A" && v !== "");
      return first ? getAsset(first) : null;
    }
    if (typeof img === "string") {
      const str = img.trim();
      return str && str !== "N/A" && str !== "" ? getAsset(str) : null;
    }
    return null;
  };

  const getCardDetails = (box) => {
    const isSent = activeTab === "sent";

    const shopId =
      (isSent ? box?.recievershopid : box?.sendershopid) ||
      (isSent ? box?.recieverShopContextId : box?.senderShopContextId);
    const shopName =
      (isSent
        ? box?.recievershopname || box?.recieverShopContextName
        : box?.sendershopname || box?.senderShopContextName) || "";
    const shopImage = isSent
      ? box?.recievershopimage || box?.recieverShopContextImage
      : box?.sendershopimage || box?.senderShopContextImage;
    const personName =
      (isSent ? box?.receiverUser?.fullname : box?.senderUser?.fullname) || "";
    const personImage = isSent
      ? box?.receiverUser?.imageUrl
      : box?.senderUser?.imageUrl;

    const hasShop = Boolean(shopId) && shopName.trim() !== "";

    const name = hasShop ? shopName : personName || shopName || "";
    const image = hasShop
      ? resolveImage(shopImage) || resolveImage(personImage)
      : resolveImage(personImage) || resolveImage(shopImage);

    return {
      displayName: name || "Selectezi User",
      displayImage: image || dummyAvatar,
    };
  };

  /**
   * Remove a box from local state immediately (instant UI).
   * Returns a restore function — call it to undo if the server call fails.
   */
  const removeFromPending = (box) => {
    // Remove every pending row from this same sender shop (collapsed card)
    const removed = localPending.filter((item) => sameSenderShop(item, box));
    removed.forEach((row) => {
      dismissedPendingRef.current.add(partyKey(row));
    });
    setLocalPending((prev) =>
      prev.filter((item) => !sameSenderShop(item, box)),
    );
    return () => {
      removed.forEach((row) => {
        dismissedPendingRef.current.delete(partyKey(row));
      });
      setLocalPending((prev) => [...removed, ...prev]);
    };
  };

  const removeFromRequested = (box) => {
    const removed = localRequested.filter((item) =>
      sameReceiverShop(item, box),
    );
    removed.forEach((row) => {
      dismissedRequestedRef.current.add(partyKey(row));
    });
    setLocalRequested((prev) =>
      prev.filter((item) => !sameReceiverShop(item, box)),
    );
    return () => {
      removed.forEach((row) => {
        dismissedRequestedRef.current.delete(partyKey(row));
      });
      setLocalRequested((prev) => [...removed, ...prev]);
    };
  };

  const handleConnect = async (box) => {
    // Accept all pending rows from this sender shop (to every one of our profiles)
    const siblings = (pendingData?.pending || localPending).filter((item) =>
      sameSenderShop(item, box),
    );
    const targets = siblings.length ? siblings : [box];
    const restore = removeFromPending(box);
    try {
      let accepted = 0;
      let lastError = null;
      for (const row of targets) {
        const payload = buildConnectionPayload(row);
        if (!payload.senderid || !payload.recieverid || !payload.businessType) {
          lastError = {
            data: {
              message:
                "Missing connection details. Please refresh and try again.",
            },
          };
          continue;
        }
        try {
          await acceptConnection(payload).unwrap();
          accepted += 1;
        } catch (err) {
          // Ignore already-handled / missing duplicate rows after first success
          const status = err?.status || err?.data?.status;
          const msg = String(err?.data?.message || "");
          if (status === 404 && /not found/i.test(msg) && accepted > 0) {
            continue;
          }
          lastError = err;
        }
      }
      if (accepted > 0) {
        SuccessNotify(
          accepted > 1
            ? `Connection Accepted (${accepted})`
            : "Connection Accepted",
        );
        // Cache is patched optimistically by the mutation; avoid an immediate
        // refetch race that can briefly restore stale pending rows.
      } else {
        restore();
        FailedNotify(lastError?.data?.message || "Something went wrong");
        console.error("Error accepting connection:", lastError);
      }
    } catch (error) {
      restore();
      FailedNotify(error?.data?.message || "Something went wrong");
      console.error("Error accepting connection:", error);
    }
  };

  const handleReject = async (box) => {
    const siblings = (pendingData?.pending || localPending).filter((item) =>
      sameSenderShop(item, box),
    );
    const targets = siblings.length ? siblings : [box];
    const restore = removeFromPending(box);
    try {
      let done = 0;
      let lastError = null;
      for (const row of targets) {
        const payload = buildConnectionPayload(row);
        if (!payload.senderid || !payload.recieverid) {
          lastError = { data: { message: "Missing connection details." } };
          continue;
        }
        try {
          await rejectConnection(payload).unwrap();
          done += 1;
        } catch (err) {
          const status = err?.status || err?.data?.status;
          if (status === 404 && done > 0) continue;
          lastError = err;
        }
      }
      if (done > 0) {
        SuccessNotify("Connection Rejected");
      } else {
        restore();
        if (lastError?.status !== 404 && lastError?.data?.status !== 404) {
          FailedNotify(lastError?.data?.message || "Something went wrong");
        }
        console.error("Error rejecting connection:", lastError);
      }
    } catch (error) {
      restore();
      FailedNotify(error?.data?.message || "Something went wrong");
      console.error("Error rejecting connection:", error);
    }
  };

  const handleCancel = async (box) => {
    const siblings = (pendingData?.requested || localRequested).filter((item) =>
      sameReceiverShop(item, box),
    );
    const targets = siblings.length ? siblings : [box];
    const restore = removeFromRequested(box);
    try {
      let done = 0;
      let lastError = null;
      for (const row of targets) {
        const payload = buildConnectionPayload(row);
        if (!payload.senderid || !payload.recieverid) {
          lastError = { data: { message: "Missing connection details." } };
          continue;
        }
        try {
          await cancelConnection(payload).unwrap();
          done += 1;
        } catch (err) {
          const status = err?.status || err?.data?.status;
          if (status === 404 && done > 0) continue;
          lastError = err;
        }
      }
      if (done > 0) {
        SuccessNotify("Connection Request Canceled");
      } else {
        restore();
        if (lastError?.status !== 404 && lastError?.data?.status !== 404) {
          FailedNotify(lastError?.data?.message || "Something went wrong");
        }
        console.error("Error canceling connection:", lastError);
      }
    } catch (error) {
      restore();
      FailedNotify(error?.data?.message || "Something went wrong");
      console.error("Error canceling connection:", error);
    }
  };

  const isBusy = isAccepting || isRejecting || isCanceling;

  // Collapse same shop → many of our profiles into one card
  const dataToMap =
    activeTab === "sent"
      ? dedupeByParty(localRequested, "requested")
      : dedupeByParty(localPending, "pending");

  return (
    <Row className="px-3">
      <div
        className={
          location.pathname === "/RestaurantAlldata"
            ? ""
            : `noscroll manage-height`
        }
      >
        <div className="cards-scroll-wrapper">
          <Row>
            {loading === true ? (
              <>
                <CandidateSkeletonCard />
                <CandidateSkeletonCard />
                <CandidateSkeletonCard />
                <CandidateSkeletonCard />
              </>
            ) : !dataToMap || dataToMap.length === 0 ? (
              <div
                className="d-flex flex-column justify-content-center align-items-center w-100 text-center"
                style={{ minHeight: "200px", marginTop: "50px" }}
              >
                <SadIcon />
                <h4 className="text-muted mb-1 mt-3">
                  No{" "}
                  {activeTab === "sent"
                    ? "Sent Requests"
                    : "Connection request"}
                </h4>
                <p className="text-muted mb-0" style={{ fontSize: "14px" }}>
                  Nothing is available to display right now.
                </p>
              </div>
            ) : (
              dataToMap.map((box, idx) => {
                const { displayName, displayImage } = getCardDetails(box);
                const platformLabels = getPlatformLabels(box);
                return (
                  <Col
                    md={3}
                    lg={3}
                    className="px-md-1 pb-4"
                    key={box._id || box.id || idx}
                  >
                    <Card className="border pt-2 pb-2 h-100">
                      <div className="py-1 d-flex flex-column align-items-center justify-content-between h-100">
                        <div>
                          <div className="candidateImg-2 m-auto">
                            <img
                              src={displayImage}
                              alt={displayName}
                              className="h-100 w-100"
                              crossOrigin="anonymous"
                              onError={(e) => {
                                e.currentTarget.onerror = null;
                                e.currentTarget.src = dummyAvatar;
                              }}
                            />
                          </div>
                          <h6 className="candidateName mb-0 mt-3 text-center">
                            {displayName}
                          </h6>
                          {platformLabels.map((label) => (
                            <p
                              key={label}
                              className="text-muted text-center mb-0 mt-1"
                              style={{ fontSize: "11px" }}
                            >
                              {label}
                            </p>
                          ))}
                        </div>

                        <div className="d-flex justify-content-center mt-3 w-100">
                          {activeTab === "sent" ? (
                            <button
                              type="button"
                              className="connect-btn-one ps-3 pe-3"
                              disabled={isBusy}
                              onClick={() => handleCancel(box)}
                            >
                              Cancel Request
                            </button>
                          ) : (
                            <>
                              <button
                                type="button"
                                className="connect-btn ps-3 pe-3"
                                disabled={isBusy}
                                onClick={() => handleConnect(box)}
                              >
                                Accept
                              </button>
                              <button
                                className="connect-btn-one ps-3 pe-3 ms-1"
                                disabled={isBusy}
                                onClick={() => handleReject(box)}
                              >
                                Ignore
                              </button>
                            </>
                          )}
                        </div>
                      </div>
                    </Card>
                  </Col>
                );
              })
            )}
          </Row>
        </div>
      </div>

      {/* Pagination */}
      {pendingData?.length > 0 && (
        <Col className="pt-2 mx-auto d-flex justify-content-center mt-5">
          <Pagination>
            <Pagination.First />
            <Pagination.Prev />
            <Pagination.Item>{1}</Pagination.Item>
            <Pagination.Ellipsis />
            <Pagination.Item>{10}</Pagination.Item>
            <Pagination.Item>{11}</Pagination.Item>
            <Pagination.Item active>{12}</Pagination.Item>
            <Pagination.Item>{13}</Pagination.Item>
            <Pagination.Item disabled>{14}</Pagination.Item>
            <Pagination.Ellipsis />
            <Pagination.Item>{20}</Pagination.Item>
            <Pagination.Next />
            <Pagination.Last />
          </Pagination>
        </Col>
      )}
    </Row>
  );
};

export default CandidatesBox;
